<script>
import { mapActions } from 'vuex';
import { cloneDeep, result } from 'lodash';
import RbxRemoteCombobox from '@/shared/reybex/RbxRemoteCombobox';
import RbxRemoteComboboxMultiple from '@/shared/reybex/RbxRemoteComboboxMultiple';
import validatorMixin from '@/mixins/validator';
import snackbarMixin from '@/mixins/snackbar';
import RbxTextEditor from '../../../shared/reybex/RbxTextEditor';

export default {
  name: 'NewAffiliateDialog',
  components: {
    RbxRemoteCombobox,
    RbxRemoteComboboxMultiple,
    RbxTextEditor,
  },
  mixins: [snackbarMixin, validatorMixin],
  data() {
    return {
      states: ['', 'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hessen',
        'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen', 'Rheinland-Pfalz', 'Saarland',
        'Sachsen', 'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen'],
      visible: false,
      isLoading: false,
      rules: {
        affiliateType: { required: true },
      },
      baseFormData: {
        affiliateType: '',
        firm: '',
        salutation: '',
        firstname: '',
        surname: '',
        city: '',
        zipcode: '',
        street: '',
        fax: '',
        mobile: '',
        phone: '',
      },
      formData: {},
    };
  },
  created() {
    this.formData = cloneDeep(this.baseFormData);
  },
  mounted() {
    this.visible = true;
  },
  beforeDestroy() {
    /* istanbul ignore next */
    this.visible = false;
  },
  methods: {
    ...mapActions('affiliateDetails', ['saveAffiliate']),
    ...mapActions('affiliateList', { fetchData: 'fetchAffiliateList' }),
    submit(type) {
      if (!this.validateForm()) return false;

      this.isLoading = true;

      return this.saveAffiliate(this.formData)
        .then(({ data }) => {
          this.showSuccessMessage('Vertragspartner erfolgreich erstellt!');

          if (type === 'new') {
            this.formData = cloneDeep(this.baseFormData);
          } else {
            this.close();

            if (type === 'detail') {
              this.$router.push(`/affiliate/${data.id}`);
            }
          }
          this.fetchData();
        })
        .catch(({ response }) => {
          const message = result(response, 'data.data.error', 'Fehler beim Erstellen des Vertragspartners. Bitte versuche es erneut!');
          this.showErrorMessage(message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    close() {
      this.$emit('cancel');
    },
  },
};
</script>

<template>
  <v-dialog v-model="visible"
            persistent
            max-width="1000px">

    <v-card>
      <v-card-title class="title pa-3">
        Neuer Vertragspartner
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md class="pa-0">
          <v-layout row wrap>
            <v-flex md12 xs12>
              <h4 class="affiliate-detail-tab-title">Allgemein</h4>
              <v-layout row wrap>

                <v-flex xs12 md4 py-0>
                  <rbx-remote-combobox
                    v-model="formData.affiliateType"
                    :error-messages="validationErrorMessages.affiliateType"
                    label="Art"
                    height="22"
                    domain="affiliateType"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.firm"
                    label="Firma"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0/>

                <v-flex xs12 md4 py-0>
                  <rbx-remote-combobox
                    v-model="formData.salutation"
                    label="Anrede"
                    height="22"
                    domain="salutation"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.firstname"
                    label="Vorname"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.surname"
                    label="Nachname"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md12 py-0>
                  <rbx-text-editor
                    label="Kommentar"
                    v-model="formData.comment"
                  />
                </v-flex>

              </v-layout>

              <h4 class="affiliate-detail-tab-title">Adressdaten</h4>
              <v-layout row wrap>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.street"
                    label="Straße"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.zip"
                    label="PLZ"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.location"
                    label="Ort"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.country"
                    label="Land"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-combobox
                    v-model="formData.state"
                    :items="states"
                    label="Bundesland"
                    height="22"
                    domain="affiliateType"
                    class="caption"
                  />
                </v-flex>

              </v-layout>

              <h4 class="affiliate-detail-tab-title">Kontaktdaten</h4>
              <v-layout row wrap>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.phone"
                    label="Telefon"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.phone2"
                    label="Telefon 2"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.mobile"
                    label="Handy"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.fax"
                    label="Fax"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.email"
                    label="E-Mail"
                    height="22"
                    class="caption"
                  />
                </v-flex>

                <v-flex xs12 md4 py-0>
                  <v-text-field
                    v-model="formData.web"
                    label="Webseite"
                    height="22"
                    class="caption"
                  />
                </v-flex>

              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn @click.native="close"
               color="red"
               flat>
          Schließen
        </v-btn>

        <v-btn @click.native="submit"
               color="blue"
               :loading="isLoading"
               flat>
          Speichern
        </v-btn>

        <v-menu bottom left>
          <v-btn slot="activator" icon>
            <v-icon>expand_more</v-icon>
          </v-btn>

          <v-list>

            <v-list-tile>
              <v-list-tile-title @click="submit('new')">
                Speichern &amp; Neu
              </v-list-tile-title>
            </v-list-tile>

            <v-list-tile>
              <v-list-tile-title @click="submit('detail')">
                Speichern &amp; Detail
              </v-list-tile-title>
            </v-list-tile>

          </v-list>
        </v-menu>

      </v-card-actions>
    </v-card>

  </v-dialog>
</template>
